import type {
  OptionMenuItemType,
  OptionType,
} from '@/components/option-menu/option-menu';
import { PropertyGroups } from '@/components/queryBuilder/propertyGroups';
import type { FilterProperty } from '@/types/advanceFilter';

/** Also the row's `data-testid`; `filterStrip.driver.ts` hovers rows by this prefix. */
const FILTER_GROUP_OPTION_ID_PREFIX = 'filter-property-group-';

// A leaf's id is its display name because the strip resolves a pick back to its
// property by display name, and because Playwright drivers address filter menu
// entries by the name the user sees.
const leafOption = (
  property: FilterProperty,
  text: string
): OptionMenuItemType => ({
  id: property.displayName,
  text,
  menuItemProps: { sx: { minWidth: '150px' } },
});

const groupOption = (
  id: string,
  title: string,
  subMenu: OptionType[]
): OptionMenuItemType => ({
  id: `${FILTER_GROUP_OPTION_ID_PREFIX}${id}`,
  text: title,
  subMenu,
  menuItemProps: { sx: { minWidth: '150px' } },
});

const divider = (beforeGroup: string): OptionType => ({
  id: `${FILTER_GROUP_OPTION_ID_PREFIX}divider${beforeGroup ? `-${beforeGroup}` : ''}`,
  divider: true,
});

// Properties that declare `condition.subMenu` (e.g. tag keys and tag key-value
// pairs) collapse into one fly-out of their own, nested inside their category.
// Tracked by group name rather than by looking at the previous entry, so members
// listed apart from each other still land in a single row: two rows sharing a
// name would also share a `data-testid`, and each would hold half the entries.
const withSubMenuGroups = (properties: FilterProperty[]): OptionType[] => {
  const options: OptionType[] = [];
  const groups = new Map<string, OptionMenuItemType>();

  for (const property of properties) {
    const subMenu = property.condition?.subMenu;
    if (!subMenu) {
      options.push(leafOption(property, property.displayName));
      continue;
    }

    const child = leafOption(property, subMenu.label);
    const group = groups.get(subMenu.group);

    if (group) {
      group.subMenu?.push(child);
      continue;
    }

    const newGroup = groupOption(subMenu.group, subMenu.group, [child]);
    groups.set(subMenu.group, newGroup);
    options.push(newGroup);
  }

  return options;
};

/**
 * Builds the add-filter menu: properties outside any category first, then one
 * fly-out per category, in `PropertyGroups` order.
 *
 * `allProperties` is the surface's full property set rather than the still
 * available ones, so the menu keeps its shape as filters are added and
 * committed instead of flattening once a category empties out.
 *
 * `groupProperties` is the surface's own call: a list short enough to read at a
 * glance is quicker to pick from flat than behind fly-outs, however many
 * categories its properties happen to name.
 */
export const buildFilterPropertyOptions = (
  availableProperties: FilterProperty[],
  allProperties: FilterProperty[],
  groupProperties: boolean
): OptionType[] => {
  if (!groupProperties) {
    return withSubMenuGroups(availableProperties);
  }

  const groupKeys = Object.keys(PropertyGroups).filter((key) =>
    allProperties.some((property) => property.group === key)
  );

  // A surface whose properties all land in one category gains nothing from a
  // fly-out that every property hides behind, so it stays a flat list.
  if (groupKeys.length < 2) {
    return withSubMenuGroups(availableProperties);
  }

  const ungrouped = availableProperties.filter(
    (property) => !groupKeys.includes(property.group)
  );

  const groups: OptionType[] = [];
  for (const key of groupKeys) {
    const properties = availableProperties.filter(
      (property) => property.group === key
    );
    if (!properties.length) {
      continue;
    }
    if (PropertyGroups[key].startsSection && groups.length) {
      groups.push(divider(key));
    }
    groups.push(
      groupOption(key, PropertyGroups[key].title, withSubMenuGroups(properties))
    );
  }

  const ungroupedDivider: OptionType[] =
    ungrouped.length && groups.length ? [divider('')] : [];

  return [...withSubMenuGroups(ungrouped), ...ungroupedDivider, ...groups];
};
